import { createSlice } from '@reduxjs/toolkit'
import { CompanyPayment } from '@leadgen/models/client'
import authActions from '../auth/actions'
import actions from './actions'

export interface CompanyPaymentsState {
  companyPayments: CompanyPayment[]
  companyPaymentIds: Record<string, boolean>
  error?: string
  status: 'initial' | 'idle' | 'loading' | 'loading-more' | 'failed'
}

export const initialState: CompanyPaymentsState = {
  companyPayments: [],
  companyPaymentIds: {},
  status: 'initial',
}

export const companyPaymentsSlice = createSlice({
  name: 'companyPayments',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(authActions.loggedOut.type, () => {
        return initialState
      })
      .addCase(actions.loadCompanyPaymentsFresh.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.loadCompanyPaymentsFresh.fulfilled, (state, action) => {
        state.status = 'idle'
        state.companyPayments = action.payload.data
        state.companyPaymentIds = action.payload.data.reduce(
          (memo, next) => {
            memo[next.id] = true
            return memo
          },
          <Record<string, boolean>>{},
        )
        state.error = undefined
      })
      .addCase(actions.loadCompanyPaymentsFresh.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.loadCompanyPaymentsNext.pending, state => {
        state.status = 'loading-more'
      })
      .addCase(actions.loadCompanyPaymentsNext.fulfilled, (state, action) => {
        state.status = 'idle'
        state.companyPayments.push(...action.payload.data)
        state.companyPaymentIds = action.payload.data.reduce((memo, next) => {
          memo[next.id] = true
          return memo
        }, state.companyPaymentIds)
        state.error = undefined
      })
      .addCase(actions.loadCompanyPaymentsNext.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(actions.addCompanyPayment, (state, action) => {
        if (state.companyPaymentIds[action.payload.id]) {
          return
        }
        if (!state.companyPayments.length) {
          state.companyPaymentIds[action.payload.id] = true
          state.companyPayments.push(action.payload)
          return
        }
        for (let i = 0, length = state.companyPayments.length; i < length; i++) {
          const planningRecord = state.companyPayments[i]
          if (new Date(action.payload.datePaid) > new Date(planningRecord.datePaid)) {
            state.companyPaymentIds[action.payload.id] = true
            state.companyPayments.splice(i, 0, action.payload)
            return
          }
        }
        state.companyPaymentIds[action.payload.id] = true
        state.companyPayments.push(action.payload)
      })
      .addCase(actions.updateCompanyPayment, (state, action) => {
        for (let i = 0, length = state.companyPayments.length; i < length; i++) {
          const companyPayment = state.companyPayments[i]
          if (companyPayment.id === action.payload.id) {
            state.companyPayments[i] = action.payload
            break
          }
        }
      })
  },
})

export default companyPaymentsSlice
